import React, { useState, useContext, useEffect, useLayoutEffect } from 'react';
import {
  Alert,
  AppHeader,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@ace-clublabs/shared-component-library';
import { MembershipAppStateContext } from '../components/MembershipAppState/MembershipAppState';
import { numberHelper } from '../utils/numberHelper';
import { Pages } from '@lib/types';
import { LoadingIndicator } from '../components/LoadingIndicator';
import { CurrentPayPlan, FlowType } from '@ace-clublabs/common-membership-api-types';
import { RemoveWarningModal, RemoveWarningModalContentProps } from '../components/RemoveWarningModal';
import { Icon } from '@ace-clublabs/shared-component-library';
import { isAutopayRequired, isAutopayAllowed, isFullDigitalRequired, getProgressProps } from '../utils/helpers';
import { UtagType } from '../utils/UtagHelper';
import { AppProgressHeader } from '../components/AppProgressHeader';
import { ContactSupportCard } from '../components/ContactSupportCard';
import { TaxSection } from '../components/ReviewPageComponents/TaxSection';
import { LeadGenerationBanner } from '../components/ReviewPageComponents/LeadGenerationBanner';
import { EnrollAutopayDisclaimer } from '../components/ReviewPageComponents/EnrollAutopayDisclaimer';
import { PaperlessDisclaimer } from '../components/ReviewPageComponents/PaperlessDisclaimer';
import { RecurringItemsSection } from '../components/ReviewPageComponents/RecurringItemsSection';
import { OneTimeItemsSection } from '../components/ReviewPageComponents/OneTimeItemsSection';

export const ReviewPage: React.FC = () => {
  const {
    membershipInfo,
    sessionData,
    refreshSession,
    loadingInfo,
    sendTag,
    updateBillPlan,
    isPriceCartLoading,
    getPaymentManifestId,
  } = useContext(MembershipAppStateContext);

  const [deleteInfo, setDeleteInfo] = useState<RemoveWarningModalContentProps | null>(null);
  const [isTagging, setIsTagging] = useState(true);
  const isGiftFlow = sessionData.flowType === FlowType.GIFT;
  const reviewTitle = isGiftFlow ? "Let's review your gift membership" : `Let's review your membership`;
  const displayLeadGeneration = sessionData.formData.mobileConsent && membershipInfo?.insuranceLeadGeneration?.enabled;

  const userAgent = navigator.userAgent || '';
  const isAndroid = /android/i.test(userAgent);
  const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream;

  let appStoreLink;
  if (isAndroid) {
    appStoreLink =
      'https://play.google.com/store/apps/details?id=com.ace.shell.production&referrer=utm_source%3DACE%26utm_campaign%3DAuto_Club_App_Landing';
  } else if (isIOS) {
    appStoreLink = 'https://apps.apple.com/app/apple-store/id1094393125?pt=205244';
  } else {
    appStoreLink = 'https://www.ace.aaa.com/automotive/driver-resources/auto-club-app.html';
  }

  useEffect(() => {
    refreshSession();
  }, []);

  useLayoutEffect(() => {
    if (!membershipInfo?.benefits) {
      return;
    }
    const tagPage = sessionData.flowType === FlowType.GIFT ? 'membership:gift:review' : 'review';
    sendTag(UtagType.view, tagPage);
    setIsTagging(false);
  }, [membershipInfo]);

  const showAutopayEnrollment =
    !isAutopayRequired(membershipInfo!, sessionData.formData) && isAutopayAllowed(membershipInfo!);

  const showFullDigitalDisclaimer = isFullDigitalRequired(membershipInfo?.paperlessOptOuts);

  const salesTax = sessionData?.priceCart?.salesTax || 0;

  const totalSaving = sessionData?.priceCart?.savings || 0;

  const isMonthly = sessionData.formData.billPlan === CurrentPayPlan.RECURRINGMONTHLY;

  const handleCta = async (updatedBillPlan: CurrentPayPlan): Promise<void> => {
    const shouldUpdateBillPlan = !isGiftFlow && updatedBillPlan !== sessionData.formData.billPlan;
    if (shouldUpdateBillPlan) {
      await updateBillPlan(updatedBillPlan);
    }
    getPaymentManifestId();
  };

  const getTotalCost = (): string => {
    const defaultCost = 0.0;
    return numberHelper.formatMoney(sessionData?.priceCart?.total ?? defaultCost);
  };

  const progressProps = getProgressProps(sessionData.flowType!, Pages.ReviewPage);

  const totalSavingBanner = totalSaving > 0 && (
    <Alert
      sx={{ background: '#FAF5FF' }}
      displayCloseButton={false}
      icon={<Icon iconName="circle-dollar-to-slot-duotone" sx={{ color: '#7527B9', fontSize: '1.5rem' }} />}
      className="total-saving-banner"
    >
      <Typography variant="b1Regular">
        You're saving <strong>{numberHelper.formatMoney(totalSaving)} </strong>today
      </Typography>
    </Alert>
  );

  const isLoading = loadingInfo.isLoading || membershipInfo?.benefits == null;
  if (isLoading) {
    return (
      <>
        <AppHeader sx={{ padding: 0 }} />
        <LoadingIndicator loadingInfo={loadingInfo} maxSteps={0} currentStep={loadingInfo.loadingDetails.length} />
      </>
    );
  }

  if (isTagging) {
    return <></>;
  }

  return (
    <>
      <AppProgressHeader {...progressProps} />
      <Typography variant="h5" className="review-header">
        {reviewTitle}
      </Typography>
      {totalSavingBanner}
      <Box className="overview-container">
        {/* Recurring items section */}
        <RecurringItemsSection />

        {/* One-time items section */}
        <OneTimeItemsSection />

        {/* Tax item */}
        {salesTax > 0 && <TaxSection />}

        {/* Total cost */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          className="total-container"
          marginBottom="1.5rem"
        >
          <Typography variant="h6" className="total-title" sx={{ '& sup': { verticalAlign: 'top' } }}>
            {isMonthly ? (
              <>
                Monthly payment<sup>*</sup>
              </>
            ) : (
              <>
                Annual total<sup>*</sup>
              </>
            )}
          </Typography>
          {isPriceCartLoading ? (
            <CircularProgress variant="indeterminate" size={25} className="total-cost-spinner" />
          ) : (
            <Typography variant="h6" className="total-price">
              {getTotalCost()}
            </Typography>
          )}
        </Stack>

        {/* Lead generation section */}
        {displayLeadGeneration && <LeadGenerationBanner />}

        {/* Autopay disclaimer section */}
        <EnrollAutopayDisclaimer />

        {/*Agree and continue button*/}
        <Box className="review-page-button">
          <Button
            label={
              <>
                {isPriceCartLoading ? (
                  <Box className="button-spinner">
                    <CircularProgress variant="indeterminate" size={20} color="inherit" />{' '}
                  </Box>
                ) : (
                  <></>
                )}
                Agree and continue
              </>
            }
            disabled={isPriceCartLoading}
            fullWidth
            onClick={async () => {
              handleCta(isMonthly ? CurrentPayPlan.RECURRINGMONTHLY : CurrentPayPlan.RECURRINGCREDITORDEBITCARD);
            }}
            variant="contained"
            size="medium"
            id="nextButton"
          ></Button>
        </Box>

        {/*Skip autopay button*/}
        {showAutopayEnrollment && (
          <>
            <Box className="skip-and-pay-button" style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}>
              <Button
                style={{ backgroundColor: 'white', color: '#016BCD' }}
                label={
                  <>
                    {isPriceCartLoading ? (
                      <Box className="button-spinner">
                        <CircularProgress variant="indeterminate" size={20} color="inherit" />{' '}
                      </Box>
                    ) : (
                      <></>
                    )}
                    Skip and pay
                  </>
                }
                disabled={isPriceCartLoading}
                fullWidth
                onClick={async () => {
                  handleCta(CurrentPayPlan.ONETIME);
                }}
                variant="contained"
                size="medium"
                id="skipNextButton"
              ></Button>
            </Box>
          </>
        )}

        {/**Paperless disclaimer section */}
        {showFullDigitalDisclaimer && <PaperlessDisclaimer />}
        {/**Contact support card */}
        <ContactSupportCard />
      </Box>
      <RemoveWarningModal
        title={deleteInfo?.title ?? ''}
        warningContent={deleteInfo?.warningContent ?? ''}
        showModal={!!deleteInfo}
        closeModal={() => setDeleteInfo(null)}
        confirmRemove={() => {
          deleteInfo?.confirmRemove();
        }}
      />
    </>
  );
};
